import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { PublicNavbar } from '@/components/layout/PublicNavbar';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { PageHero, getServiceIcon } from '@/components/layout/PublicSections';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Service, Branch } from '@/types';
import { MONKEY_FRANCHISE_ID } from '@/lib/config';
import { MapPin, Clock, Loader2, Scissors } from 'lucide-react';

export default function PublicServices() {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch branches from the specific franchise only
        const branchesQuery = query(
          collection(db, 'branches'),
          where('franchiseId', '==', MONKEY_FRANCHISE_ID),
          where('isActive', '==', true)
        );
        const branchesSnapshot = await getDocs(branchesQuery);
        const branchesData = branchesSnapshot.docs.map(doc => ({
          branchId: doc.id,
          ...doc.data()
        } as Branch));
        setBranches(branchesData);

        // Set first branch as default if exists
        if (branchesData.length > 0 && !selectedBranchId) {
          setSelectedBranchId(branchesData[0].branchId);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        // Fetch all active services from the franchise
        const servicesQuery = query(
          collection(db, 'services'),
          where('franchiseId', '==', MONKEY_FRANCHISE_ID),
          where('isActive', '==', true)
        );
        const querySnapshot = await getDocs(servicesQuery);
        const servicesData = querySnapshot.docs.map(doc => ({
          serviceId: doc.id,
          ...doc.data()
        } as Service));

        // Sort by displayOrder (set from admin), then by name as fallback
        const sortedServices = servicesData.sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name);
        });
        setServices(sortedServices);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <title>{t('publicServices.seo.title')}</title>
      </Helmet>
      <PublicNavbar />

      <PageHero
        eyebrow={t('publicServices.hero.subtitle')}
        title={t('publicServices.hero.title')}
      >
        {branches.length > 1 && (
          <div className="mx-auto max-w-sm text-left">
            <label
              htmlFor="branch-selector"
              className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-subtle"
            >
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {t('publicServices.branchLabel', 'Sucursal')}
            </label>
            <select
              id="branch-selector"
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
              className="w-full rounded-lg border border-hairline bg-surface-3 px-4 py-2.5 font-medium text-ink outline-none focus:border-transparent focus:ring-2 focus:ring-focus"
            >
              {branches.map(branch => (
                <option key={branch.branchId} value={branch.branchId}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </PageHero>

      {/* Services grid */}
      <section className="bg-surface py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          {loading ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
              <p className="mt-4">{t('publicServices.loading')}</p>
            </div>
          ) : services.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <Scissors className="h-8 w-8" aria-hidden="true" />
              <p className="mt-4">{t('publicServices.empty')}</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((service) => {
                const Icon = getServiceIcon(service);
                return (
                  <div
                    key={service.serviceId}
                    className="flex flex-col overflow-hidden rounded-xl border border-hairline bg-canvas transition-colors hover:border-accent/60"
                  >
                    {service.imageUrl ? (
                      <div className="h-48 overflow-hidden border-b border-hairline">
                        <OptimizedImage
                          src={service.imageUrl}
                          alt={service.name}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="flex h-48 items-center justify-center border-b border-hairline bg-surface-2">
                        <Icon className="h-12 w-12 text-ink-subtle" aria-hidden="true" />
                      </div>
                    )}

                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-sans text-lg font-semibold tracking-tight text-ink">{service.name}</h3>
                      {service.description && (
                        <p className="mt-2 line-clamp-2 text-sm text-ink-muted">{service.description}</p>
                      )}

                      {/* Pinned footer keeps every card aligned regardless of copy length */}
                      <div className="mt-auto pt-5">
                        <div className="flex items-center justify-between border-t border-hairline pt-4">
                          <span className="flex items-center gap-1.5 text-sm text-ink-subtle">
                            <Clock className="h-4 w-4" aria-hidden="true" />
                            {service.duration} {t('publicServices.duration')}
                          </span>
                          <span className="text-2xl font-semibold tabular-nums text-ink">
                            {(service.price / 100).toFixed(0)}€
                          </span>
                        </div>
                        <Link to="/take-ticket" className="mt-4 block">
                          <Button variant="primary" className="w-full">
                            {t('publicServices.bookNow')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
